import { Outlet, useLocation } from '@tanstack/react-router';
import { AppHeader } from './AppHeader';
import { ErrorBoundary } from './ErrorBoundary';
import { ImpersonationBanner } from './ImpersonationBanner';
import { OnboardingBanner } from './OnboardingBanner';
import { ShortcutSheet } from './ShortcutSheet';
import { useGlobalShortcuts } from './useGlobalShortcuts';
import { reportError } from './telemetry';

/**
 * Top-level layout. Renders the persistent AppHeader, the active route
 * inside an ErrorBoundary (keyed by pathname so navigation auto-resets
 * after a crash), and the ShortcutSheet modal that the global `?`
 * keypress toggles.
 *
 * Routes opt out of the shell via `staticData: ROUTE_META.fullscreen`
 * (e.g. `/auth`) — that check lives in __root.tsx; this component
 * trusts that it's being rendered for shell-eligible routes only.
 */
export function AppShell() {
  const { pathname } = useLocation();
  const { shortcutSheetOpen, setShortcutSheetOpen } = useGlobalShortcuts();

  return (
    <div className="flex min-h-screen flex-col bg-gray-50 text-gray-900">
      <AppHeader />
      <ImpersonationBanner />
      <OnboardingBanner />
      {/* mx-auto + max-w-7xl gives every screen the same content
          column (1280px), regardless of which one ships its own
          inner container. Per-screen `mx-auto max-w-*` wrappers
          would double-center and shrink the workbench; they were
          removed when this consolidation landed. */}
      <main className="mx-auto w-full max-w-7xl flex-1 px-sp-6 py-sp-6">
        <ErrorBoundary key={pathname} onError={reportError}>
          <Outlet />
        </ErrorBoundary>
      </main>
      <ShortcutSheet
        open={shortcutSheetOpen}
        onClose={() => setShortcutSheetOpen(false)}
      />
    </div>
  );
}
